/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {Dispatch, FC, SetStateAction, createContext, useCallback, useContext, useEffect, useState} from 'react'
import {WithChildren} from '../../helpers'

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

// title/description/breadcrumbs/showBizPicker are set together as a single unit so a page
// transition can never commit a render where some fields reflect the new page and others
// still reflect the previous one (was the root cause of the BizPicker/page-title overlap
// flash — see project-docs bug notes on page chrome race).
interface PageChrome {
  title: string
  description: string
  breadcrumbs: Array<PageLink>
  showBizPicker: boolean
}

const defaultPageChrome: PageChrome = {
  title: '',
  description: '',
  breadcrumbs: [],
  showBizPicker: false,
}

export interface PageDataContextModel {
  pageTitle?: string
  pageDescription?: string
  pageBreadcrumbs?: Array<PageLink>
  showBizPicker?: boolean
  setPageChrome: Dispatch<SetStateAction<PageChrome>>
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageChrome: () => {},
})

const PageDataProvider: FC<WithChildren> = ({children}) => {
  const [pageChrome, setPageChrome] = useState<PageChrome>(defaultPageChrome)
  const value: PageDataContextModel = {
    pageTitle: pageChrome.title,
    pageDescription: pageChrome.description,
    pageBreadcrumbs: pageChrome.breadcrumbs,
    showBizPicker: pageChrome.showBizPicker,
    setPageChrome,
  }
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
  showBizPicker?:boolean
}

const PageTitle: FC<Props & WithChildren> = ({children, description, breadcrumbs, showBizPicker}) => {
  const {setPageChrome} = usePageData()
  const title = children ? children.toString() : ''

  useEffect(() => {
    setPageChrome({
      title,
      description: description ?? '',
      breadcrumbs: breadcrumbs ?? [],
      showBizPicker: showBizPicker ?? false,
    })
    return () => {
      setPageChrome(defaultPageChrome)
    }
  }, [title, description, breadcrumbs, showBizPicker])

  return <></>
}

const PageDescription: FC<WithChildren> = ({children}) => {
  const {setPageChrome} = usePageData()
  useEffect(() => {
    if (children) {
      const description = children.toString()
      setPageChrome((prev) => ({...prev, description}))
      return () => {
        setPageChrome((prev) => ({...prev, description: ''}))
      }
    }
  }, [children])
  return <></>
}

export {PageDescription, PageTitle, PageDataProvider, usePageData}
